import React, { useCallback, useEffect, useRef, useState } from 'react';
import handbookService from './HandbookService';
import { Alert, Badge, Card, CardBody, Col, Collapse, Container, Row, Spinner, Button } from 'reactstrap';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import { FiAlertTriangle, FiLink, FiFileText, FiFilm, FiImage, FiChevronDown, FiChevronRight } from 'react-icons/fi';

function getEmbedVideoUrl(url) {
	if (!url) return '';
	try {
		if (url.includes('youtube.com/watch?v=')) return `https://www.youtube.com/embed/${url.split('v=')[1].split('&')[0]}`;
		if (url.includes('youtu.be/')) return `https://www.youtube.com/embed/${url.split('youtu.be/')[1].split('?')[0]}`;
		if (url.includes('vimeo.com/')) return `https://player.vimeo.com/video/${url.split('vimeo.com/')[1].split('?')[0]}`;
	} catch (e) { return ''; }
	return '';
}

function HandbookItemCard({ item, skipTitle, disableCollapsible, lang }) {
	const [isOpen, setIsOpen] = useState(!(item && item.isCollapsible));

	useEffect(() => {
		setIsOpen(!(item && item.isCollapsible));
	}, [item?.id, item?.isCollapsible]);

	if (!item) return null;

	const toggleCollapsed = () => setIsOpen(v => !v);

	const getField = (obj, enField, cyField) => (lang === 'cy' ? (obj[cyField] || obj[enField]) : (obj[enField] || obj[cyField]));

	const renderItemBody = () => {
		if (item.itemType === 'PersonCard') {
			// Support both new people[] array and legacy flat fields
			const people = item.people && item.people.length > 0
				? item.people
				: (item.personName ? [{ id: 'legacy', name: item.personName, role: item.personRole, email: item.personEmail, phone: item.personPhone, address: '', imageUrl: item.imageUrl }] : []);

			return (
				<div className="hb-person-card-group">
					{people.map(person => (
						<div key={person.id || person.name} className="hb-item hb-person-card">
							<div className="hb-person-card-inner">
								{person.imageUrl && (
									<div className="hb-person-photo">
										<img src={person.imageUrl} alt={getField(person, 'name', 'name_cy') || ''} />
									</div>
								)}
								<div className="hb-person-info">
									{(getField(person, 'name', 'name_cy')) && <h5 className="hb-person-name">{getField(person, 'name', 'name_cy')}</h5>}
									{(getField(person, 'role', 'role_cy')) && <div className="hb-person-role">{getField(person, 'role', 'role_cy')}</div>}
									{person.email && <div className="hb-person-contact"><a href={`mailto:${person.email}`}>{person.email}</a></div>}
									{person.phone && <div className="hb-person-contact">{person.phone}</div>}
									{person.address && <div className="hb-person-contact" style={{ whiteSpace: 'pre-line' }}>{person.address}</div>}
								</div>
							</div>
						</div>
					))}
					{people.length === 0 && <div className="hb-item text-muted">No contact details available.</div>}
				</div>
			);
		}

		if (item.itemType === 'Video') {
			const embedUrl = getEmbedVideoUrl(item.videoUrl || item.linkUrl);
			return (
				<div className="hb-item hb-video-item">
					{!skipTitle && (getField(item, 'title', 'title_cy')) && <h5 className="hb-item-title">{getField(item, 'title', 'title_cy')}</h5>}
					{(getField(item, 'content', 'content_cy')) && <div className="hb-item-content" dangerouslySetInnerHTML={{ __html: getField(item, 'content', 'content_cy') }} />}
					{embedUrl ? (
						<div className="hb-video-wrapper">
							<iframe title={getField(item, 'title', 'title_cy') || 'video'} src={embedUrl} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
						</div>
					) : item.videoUrl ? (
						<a href={item.videoUrl} target="_blank" rel="noopener noreferrer" className="hb-link">Watch video &rarr;</a>
					) : null}
				</div>
			);
		}

		if (item.itemType === 'Callout') {
			return (
				<div className="hb-item hb-callout">
					<div className="hb-callout-icon"><FiAlertTriangle /></div>
					<div className="hb-callout-body">
						{!skipTitle && (getField(item, 'title', 'title_cy')) && <strong>{getField(item, 'title', 'title_cy')}</strong>}
						{(getField(item, 'content', 'content_cy')) && <div dangerouslySetInnerHTML={{ __html: getField(item, 'content', 'content_cy') }} />}
					</div>
				</div>
			);
		}

		if (item.itemType === 'Link') {
			const links = item.links && item.links.length > 0 ? item.links : [];
			return (
				<div className="hb-link-grid">
					{links.map(link => (
						<div key={link.id || link.url} className="hb-item hb-link-item">
							{link.logoImageUrl && <div className="hb-link-logo"><img src={link.logoImageUrl} alt={(getField(link, 'name', 'name_cy')) || ''} /></div>}
							<div>
								<h5 className="hb-item-title mb-1">
									{(getField(link, 'url_cy', 'url_cy') || link.url) ? <a href={(getField(link, 'url', 'url_cy')) || link.url} target="_blank" rel="noopener noreferrer">{getField(link, 'name', 'name_cy') || getField(link, 'name', 'name_cy') || link.url}</a> : (getField(link, 'name', 'name_cy') || 'Untitled link')}
								</h5>
								{(getField(link, 'htmlContent', 'htmlContent_cy')) && <div className="hb-item-content" dangerouslySetInnerHTML={{ __html: getField(link, 'htmlContent', 'htmlContent_cy') }} />}
							</div>
						</div>
					))}
					{links.length === 0 && (
						<div className="hb-item hb-link-item text-muted">No links available.</div>
					)}
				</div>
			);
		}

		if (item.itemType === 'Pdf') {
			const pdfs = item.pdfs && item.pdfs.length > 0 ? item.pdfs : [];
			return (
				<div className="hb-pdf-grid">
					{pdfs.map(pdf => (
						<div key={pdf.id || pdf.url} className="hb-item hb-pdf-item">
							<div className="hb-pdf-thumb">
								{(getField(pdf, 'url', 'url_cy')) ? <iframe title={getField(pdf, 'name', 'name_cy') || 'pdf'} src={`${(getField(pdf, 'url', 'url_cy'))}#page=1&view=FitH&toolbar=0&navpanes=0&scrollbar=0`} scrolling="no" /> : <div className="hb-pdf-thumb-empty">PDF</div>}
							</div>
							<div>
								<h5 className="hb-item-title mb-1">
									{(getField(pdf, 'url', 'url_cy')) ? <a href={(getField(pdf, 'url', 'url_cy'))} target="_blank" rel="noopener noreferrer">{getField(pdf, 'name', 'name_cy') || (getField(pdf, 'url', 'url_cy'))}</a> : (getField(pdf, 'name', 'name_cy') || 'Untitled PDF')}
								</h5>
								{(getField(pdf, 'htmlContent', 'htmlContent_cy')) && <div className="hb-item-content" dangerouslySetInnerHTML={{ __html: getField(pdf, 'htmlContent', 'htmlContent_cy') }} />}
							</div>
						</div>
					))}
					{pdfs.length === 0 && (
						<div className="hb-item hb-pdf-item text-muted">No PDFs available.</div>
					)}
				</div>
			);
		}

		return (
			<div className="hb-item hb-text-item">
				{!skipTitle && (getField(item, 'title', 'title_cy')) && <h5 className="hb-item-title">{getField(item, 'title', 'title_cy')}</h5>}
				{item.imageUrl && <img src={item.imageUrl} alt={getField(item, 'title', 'title_cy') || ''} className="hb-photo" />}
				{(getField(item, 'content', 'content_cy')) && <div className="hb-item-content" dangerouslySetInnerHTML={{ __html: getField(item, 'content', 'content_cy') }} />}
				{(getField(item, 'linkUrl', 'linkUrl')) && <div><a href={(getField(item, 'linkUrl', 'linkUrl'))} target="_blank" rel="noopener noreferrer">Open link &rarr;</a></div>}
				{(getField(item, 'documentUrl', 'documentUrl')) && <div><a href={(getField(item, 'documentUrl', 'documentUrl'))} target="_blank" rel="noopener noreferrer">Open document &rarr;</a></div>}
			</div>
		);
	};

	const body = renderItemBody();
	if (!item.isCollapsible || disableCollapsible) return body;

	return (
		<div className="hb-item">
			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
				<button
					type="button"
					className="hb-nav-link"
					onClick={toggleCollapsed}
					style={{ flex: 1, textAlign: 'left', fontWeight: 600 }}
				>
					{getField(item, 'title', 'title_cy') || 'Details'}
				</button>
				<button
					type="button"
					className="hb-nav-toggle"
					onClick={toggleCollapsed}
					aria-label={isOpen ? 'Collapse item' : 'Expand item'}
				>
					{isOpen ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
				</button>
			</div>
			<Collapse isOpen={isOpen}>
				<div className="mt-2">{body}</div>
			</Collapse>
		</div>
	);
}

function slugifyAnchor(text) {
	return (text || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function SubsectionBlock({ title, items, anchorId, lang }) {
	const singleItem = items && items.length === 1 ? items[0] : null;
	const titleTogglesItem = Boolean(title && singleItem && singleItem.isCollapsible);
	const [isOpen, setIsOpen] = useState(!titleTogglesItem);

	useEffect(() => {
		setIsOpen(!titleTogglesItem);
	}, [titleTogglesItem, singleItem?.id]);

	return (
		<div className="hb-subsection" id={anchorId || undefined}>
			{title && (
				titleTogglesItem ? (
					<h4
						className="hb-subsection-title"
						onClick={() => setIsOpen(v => !v)}
						style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
					>
						{isOpen ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
						{title}
					</h4>
				) : (
					<h4 className="hb-subsection-title">{title}</h4>
				)
			)}
			{titleTogglesItem ? (
				<Collapse isOpen={isOpen}>
					<div className="hb-subsection-items">
						{items.map(item => <HandbookItemCard key={item.id} item={item} skipTitle disableCollapsible lang={lang} />)}
					</div>
				</Collapse>
			) : (
				<div className="hb-subsection-items">
					{items.map(item => <HandbookItemCard key={item.id} item={item} skipTitle lang={lang} />)}
				</div>
			)}
		</div>
	);
}

export function HandbookPage() {
	const [sections, setSections] = useState([]);
	const [activeSectionId, setActiveSectionId] = useState(null);
	const [expandedNav, setExpandedNav] = useState({});
	const [loading, setLoading] = useState(true);
	const [lang, setLang] = useState('en');
	const sectionRefs = useRef({});
	const location = useLocation();
	const params = useParams();
	const history = useHistory();

	useEffect(() => {
		// sync lang from URL param if present
		if (params && params.lang === 'cy') setLang('cy');
		else setLang('en');
	}, [params.lang]);

	const setLangAndUpdateUrl = (nextLang) => {
		setLang(nextLang);
		// update URL to include or remove trailing /cy
		let base = location.pathname.replace(/\/(en|cy)$/, '');
		const newPath = nextLang === 'cy' ? `${base}/cy` : base;
		history.replace({ pathname: newPath, search: location.search, hash: location.hash });
	};

	useEffect(() => {
		let mounted = true;
		async function load() {
			try {
				const data = await handbookService.getSections();
				if (!mounted) return;
				const sectionList = data || [];
				setSections(sectionList);
				if (sectionList.length > 0) {
					setActiveSectionId(sectionList[0].id);
					const expanded = {};
					sectionList.forEach(s => { expanded[s.id] = false; });
					setExpandedNav(expanded);
				}
			} finally {
				if (mounted) setLoading(false);
			}
		}
		load();
		return () => { mounted = false; };
	}, []);

	useEffect(() => {
		if (sections.length === 0) return;
		const hash = location.hash ? location.hash.replace('#', '') : '';
		if (hash) {
			const target = sections.find(s => s.key === hash || s.id === hash);
			if (target) {
				setActiveSectionId(target.id);
				setTimeout(() => scrollToSection(target.id), 100);
			}
		}
	}, [sections, location.hash]);

	const scrollToSection = useCallback((sectionId) => {
		const el = sectionRefs.current[sectionId];
		if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}, []);

	const scrollToAnchor = useCallback((anchorId) => {
		const el = window.document.getElementById(anchorId);
		if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}, []);

	const handleNavClick = (section) => {
		setActiveSectionId(section.id);
		scrollToSection(section.id);
	};

	const toggleNavExpand = (sectionId) => {
		setExpandedNav(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
	};

	const getGroupedItems = (section) => {
		const items = section.items || [];
		const groups = [];
		items.forEach(item => {
			const title = (lang === 'cy') ? (item.title_cy || item.title) : (item.title || item.title_cy || '');
			groups.push({ title: title, items: [item], isPersonCard: item.itemType === 'PersonCard' });
		});
		return groups;
	};

	if (loading) {
		return (
			<Container className="hb-loading">
				<Spinner /> Loading handbook...
			</Container>
		);
	}

	if (sections.length === 0) {
		return (
			<Container className="mt-4">
				<Alert color="info">The volunteer handbook is not yet available. Please check back later.</Alert>
			</Container>
		);
	}

	return (
		<div className="hb-page">
			<div className="hb-hero">
				<div className="hb-hero-inner">
					<h1>{lang === 'cy' ? 'e-lawlyfr i Wirfoddolwyr' : 'Volunteer Handbook'}</h1>
					<p>{lang === 'cy' ? 'Dogfennau, gwybodaeth a chysylltiadau allweddol' : 'Key documents, information, and contacts.'}</p>
					<div style={{ marginTop: 8 }}>
						<Button color={lang === 'en' ? 'primary' : 'light'} size="sm" onClick={() => setLangAndUpdateUrl('en')}>English</Button>
						<Button color={lang === 'cy' ? 'primary' : 'light'} size="sm" onClick={() => setLangAndUpdateUrl('cy')} style={{ marginLeft: 8 }}>Cymraeg</Button>
					</div>
				</div>
			</div>

			<Container fluid className="hb-body">
				<Row>
					<Col lg={3} md={4} className="hb-sidebar-col">
						<nav className="hb-sidebar">
							<div className="hb-sidebar-title">Contents</div>
							<ul className="hb-nav">
								{sections.map(section => {
									const groups = getGroupedItems(section);
									const titledGroups = groups.filter(g => g.title);
									const isActive = activeSectionId === section.id;
									const isExpanded = expandedNav[section.id];
									const sectionName = (lang === 'cy') ? (section.name_cy || section.name) : section.name;
									return (
										<li key={section.id} className={`hb-nav-section ${isActive ? 'active' : ''}`}>
											<div className="hb-nav-section-header">
												<button className="hb-nav-link" onClick={() => handleNavClick(section)}>
													{sectionName}
												</button>
												{titledGroups.length > 0 && (
													<button className="hb-nav-toggle" onClick={() => toggleNavExpand(section.id)} aria-label="Toggle subsections">
														{isExpanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
													</button>
												)}
											</div>
											{titledGroups.length > 0 && (
												<Collapse isOpen={isExpanded}>
													<ul className="hb-nav-sub">
														{titledGroups
															.filter((g, idx, arr) => arr.findIndex(x => x.title === g.title) === idx)
															.map(g => {
																const anchorId = `sub-${section.key}-${slugifyAnchor(g.title)}`;
																return (
																	<li key={g.title}>
																		<button className="hb-nav-sub-link" onClick={() => scrollToAnchor(anchorId)}>
																			{g.title}
																			</button>
																		</li>
																);
															})}
													</ul>
												</Collapse>
											)}
										</li>
									);
								})}
							</ul>
						</nav>
					</Col>

					<Col lg={9} md={8} className="hb-content-col">
						{sections.map(section => {
							const groups = getGroupedItems(section);
							const sectionName = (lang === 'cy') ? (section.name_cy || section.name) : section.name;
							const sectionDesc = (lang === 'cy') ? (section.description_cy || section.description) : section.description;
							return (
								<div
									key={section.id}
									className="hb-section"
									id={`section-${section.key}`}
									ref={el => { sectionRefs.current[section.id] = el; }}
								>
									<div className="hb-section-header">
										<h2 className="hb-section-title">{sectionName}</h2>
										{sectionDesc && <p className="hb-section-desc">{sectionDesc}</p>}
									</div>

									{groups.length === 0 && (
										<p className="text-muted">No content in this section yet.</p>
									)}

									{groups.length === 1 && !groups[0].title && !groups[0].isPersonCard ? (
										<div className="hb-subsection-items">
											{groups[0].items.map(item => <HandbookItemCard key={item.id} item={item} lang={lang} />)}
										</div>
									) : (
										groups.map((g, idx) => {
											const anchorId = g.title ? `sub-${section.key}-${slugifyAnchor(g.title)}` : undefined;
											return (
												<SubsectionBlock
													key={g.isPersonCard ? (g.items[0] && g.items[0].id) || `_pc_${idx}` : (g.title || `_untitled_${idx}`)}
													title={g.title}
													items={g.items}
													anchorId={anchorId}
													lang={lang}
												/>
											);
										})
									)}
								</div>
							);
						})}
					</Col>
				</Row>
			</Container>
		</div>
	);
}
